import './styles/JobOfferPeriod.scss';

import { WbSunny, NightsStay, Clear } from '@mui/icons-material';
import { Fab, Grid, Paper } from '@mui/material';

import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useJobPeriodMutation } from 'api/hooks/jobmarket/jobperiods';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { JobPeriod } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { isJobPeriodActive } from 'utils/jobPeriodHelper';

interface Props {
  period: JobPeriod;
  user: any;
  companyId: number;
  creatorId: number;
  readonly?: boolean;
  selected?: boolean;
  canceled?: boolean;
}

export default function JobOfferPeriod(props: Props) {
  const [deletingPeriod, setDeletingPeriod] = useState(false);
  const period = props.period;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const aclHelper = new ACLHelper(props.user);

  const { deletion } = useJobPeriodMutation();

  const isPeriodInFuture = new Date() <= new Date(period.startDate);
  const isPeriodActive = isJobPeriodActive(period);

  const handleDelete = async () => {
    await deletion.mutateAsync(period.id).catch(e => {
      dispatch(AlertActions.error(`${e.message}`));
    });
    setDeletingPeriod(false);
  };

  const renderDeleteButton = () => {
    const deleteButton = () => {
      return (
        <Fab
          className="job-offer-period__delete__button"
          color="primary"
          onClick={() => {
            setDeletingPeriod(true);
          }}
          size="small"
        >
          <Clear className="job-offer-period__delete__button__icon" />
        </Fab>
      );
    };

    if (aclHelper.isSuperUser() || aclHelper.isJobmarketAdmin()) {
      return deleteButton();
    }
    if (!isPeriodInFuture) {
      return;
    }
    if (
      (aclHelper.isCompanyUser() && props.user.company.id === props.companyId.toString()) ||
      (aclHelper.isEmployee() && props.creatorId === props.user.id)
    ) {
      return deleteButton();
    }
  };

  return (
    <Grid
      component={Paper}
      item
      elevation={3}
      title="Beendet"
      className={clsx('job-offer-period', {
        'job-offer-period--archived': period.archived,
        'job-offer-period--active': isPeriodActive,
        'job-offer-period--future': isPeriodInFuture,
        'job-offer-period--selected': props.selected,
        'job-offer-period--canceled': props.canceled
      })}
    >
      <Grid container className="job-offer-period__name">
        {`${period.jobPrice.name} (${t(
          translations.pages.manageJobOffers.factors[
            period.jobPrice.duration < 2
              ? period.jobPrice.factor
              : `${period.jobPrice.factor}_plural`
          ](),
          { duration: period.jobPrice.duration }
        )}, ${period.jobPrice.price}€)`}
      </Grid>
      <Grid container>
        <Grid item xs="auto" className="job-offer-period__icon">
          <WbSunny />
        </Grid>
        <Grid item xs>
          <Grid container className="job-offer-period__label">
            {t(translations.common.start())}
          </Grid>
          <Grid container>{new Date(period.startDate).toLocaleDateString()}</Grid>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs="auto" className="job-offer-period__icon">
          <NightsStay />
        </Grid>
        <Grid item xs>
          <Grid container className="job-offer-period__label">
            {t(translations.common.end())}
          </Grid>
          <Grid container>{new Date(period.endDate).toLocaleDateString()}</Grid>
        </Grid>
      </Grid>
      {props.readonly !== true && (
        <Grid container>
          <Grid item xs className="job-offer-period__delete">
            {renderDeleteButton()}
            <ConfirmationDialog
              open={deletingPeriod}
              title={t(translations.pages.manageJobOffers.deletePeriod.title())}
              onClose={() => {
                setDeletingPeriod(false);
              }}
              onConfirm={() => {
                handleDelete();
              }}
              isPending={deletion.isPending}
            >
              {t(translations.pages.manageJobOffers.deletePeriod.message())}
            </ConfirmationDialog>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
